import { createFeatureSelector, createReducer, createSelector, on } from "@ngrx/store";
import * as userActions from "./user.actions";
import { IGetUserResponse, IPageWithLayout, ISetting, IUpdateUserInfoResponse, IUserBook, IUserBooksResponse, IUserBookWithChapter, IUserBookWithChapterResponse, IUserChapterWithAllPages, IUserChapterWithPages, IUserChapterWithPagesResponse, IUserInfo, } from "../../models/user";
import { IOrderInfo, IOrderInfoPagedResult, ISubscriptionPlanWithTaxAndDiscountInfo } from "../../models/Subscription";
import { IResponse } from "../../models/admin";
import { AsyncState, AsyncStatus } from "..";
import { IErrors } from "../../models/error";

export interface IUserState {
    IsNewUser: boolean,
    UserInfo: IUserInfo | null,
    Settings: ISetting[];
    Books: { [key: string]: IUserBook[] },    // Key is "${pageNumber}_${pageSize}"
    Pagination: {
        PageNumber: number;
        PageSize: number;
        TotalCount: number;
        TotalPages: number;
    } | null;
    BookWithChapter: { [bookId: string]: IUserBookWithChapter } | null
    ChapterWithPages: { [chapterId: string]: IUserChapterWithAllPages } | null,
    loading: boolean,
    SubscriptionPlans: {
        state: AsyncState,
        plans: ISubscriptionPlanWithTaxAndDiscountInfo[]
    },
    Orders: {
        state: AsyncState,
        orders: { [key: string]: IOrderInfo[] },  // Key will be "${pageNumber}_${pageSize}_${orderStatus}_${paymentStatus}"
        pagination: {
            pageNumber: number;
            pageSize: number;
            totalCount: number;
            totalPages: number;
        } | null;
    }
}

export const initialState: IUserState = {
    IsNewUser: true,
    UserInfo: null,
    Books: {},
    Settings: [],
    Pagination: null,
    BookWithChapter: null,
    ChapterWithPages: null,
    loading: false,
    SubscriptionPlans: {
        state: { status: "NotFetched" },
        plans: []
    },
    Orders: {
        state: { status: "NotFetched" },
        orders: {},
        pagination: null
    }
}

export const userReducer = createReducer<IUserState>(
    initialState,
    on(userActions.ClearUserStateAction, () => initialState), // Reset to initial state
    on(userActions.GetUserSuccessAction, (state: IUserState, props: { response: IGetUserResponse }) => {
        return {
            ...state,
            UserInfo: props.response.Value
        };
    }),
    on(userActions.GetSettingSuccessAction, (state: IUserState, props: { response: IResponse<ISetting[]> }) => {
        return {
            ...state,
            Settings: props.response.Value
        }
    }),
    on(userActions.UpdateUserSuccessAction, (state: IUserState, props: { response: IUpdateUserInfoResponse }) => {
        return {
            ...state,
            IsNewUser: false,
            UserInfo: {
                UserId: props.response.Value.UserId ?? null,
                Fullname: props.response.Value.Fullname ?? null,
                Email: props.response.Value.Email ?? null,
                Mobile: props.response.Value.Mobile ?? null,
                Gender: props.response.Value.Gender ?? null,
                Dob: props.response.Value.Dob ?? null,
                Grade: props.response.Value.Grade ?? null,
                MotherTongue: props.response.Value.MotherTongue ?? null,
                PreferredLanguage: props.response.Value.PreferredLanguage ?? null,
                GuardianName: props.response.Value.GuardianName ?? null,
                Address: props.response.Value.Address ?? null,
                PrimaryAddress: props.response.Value.PrimaryAddress ?? null,
                PictureUrl: props.response.Value.PictureUrl ?? null,
                PrimarySubscription: null,
                Declarations: props.response.Value.Declarations ?? null,
                // The Roles and Subscription will be updating after calling the Refresh Token After SuccessFull Profile Updation
                Roles: state.UserInfo?.Roles ?? [], // Preserve existing Roles from state
                Subscriptions: state.UserInfo?.Subscriptions ?? [] // Preserve existing Subscriptions from state
            }
        }
    }),
    on(userActions.FetchUserBooksAction, (state: IUserState) => {
        return {
            ...state,
            loading: true // Set loading to true when fetching books
        };
    }),
    on(userActions.FetchUserBooksSuccessAction, (state: IUserState, props: { response: IUserBooksResponse, pageNumber: number, pageSize: number }) => {
        const key = `${props.pageNumber}_${props.pageSize}`;
        return {
            ...state,
            Books: {
                ...state.Books,
                [key]: props.response.Value || [],
            },
            Pagination: {
                PageNumber: props.response.PageNumber!,
                PageSize: props.response.PageSize!,
                TotalCount: props.response.TotalCount!,
                TotalPages: props.response.TotalPages!,
            },
            loading: false
        };
    }),
    on(userActions.FetchUserBooksFailureAction, (state: IUserState) => {
        return {
            ...state,
            loading: false // Set loading to false on failure
        };
    }),
    on(userActions.FetchBookWithChapterSuccessAction, (state: IUserState, props: { response: IUserBookWithChapterResponse }) => {
        return {
            ...state,
            BookWithChapter: {
                ...state.BookWithChapter,
                [props.response.Value.BookId!]: props.response.Value
            }
        }
    }),
    on(userActions.FetchChapterWithPagesSuccessAction, (state: IUserState, props: { response: IUserChapterWithPagesResponse }) => {
        const chapterId = props.response.Value.ChapterId!;
        const incoming = props.response.Value;
        const existingChapter = state.ChapterWithPages?.[chapterId];

        // Merge and dedupe logic for AllPages only
        const mergeUniqueAllPages = (newPages: IPageWithLayout[], existingPages: IPageWithLayout[]) => {
            const seen = new Map<string, IPageWithLayout>();

            [...existingPages, ...newPages].forEach(page => {
                const key = `${page.PageID}_${page.Index}`;
                if (!seen.has(key)) {
                    seen.set(key, page);
                }
            });

            return Array.from(seen.values()).sort((a, b) => a.Index - b.Index);
        };

        // Combine all pages from incoming (Previous + Current + Next)
        const newAllPages: IPageWithLayout[] = [
            ...incoming.PreviousPages,
            ...(incoming.CurrentPage ? [incoming.CurrentPage] : []),
            ...incoming.NextPages
        ];

        // Merge new pages with existing AllPages
        const mergedAllPages = existingChapter
            ? mergeUniqueAllPages(newAllPages, existingChapter.AllPages)
            : mergeUniqueAllPages(newAllPages, []);

        return {
            ...state,
            ChapterWithPages: {
                ...state.ChapterWithPages,
                [chapterId]: {
                    ...incoming,
                    PreviousPages: incoming.PreviousPages,
                    CurrentPage: incoming.CurrentPage,
                    NextPages: incoming.NextPages,
                    AllPages: mergedAllPages
                }
            }
        };
    }),
    on(userActions.ClearChapterWithPagesStateAction, (state: IUserState) => {
        return {
            ...state,
            ChapterWithPages: null        // Clear All Chapters with Pages State
        };
    }),
    on(userActions.FetchSubscriptionPlansAction, (state: IUserState) => {
        return {
            ...state,
            SubscriptionPlans: {
                ...state.SubscriptionPlans,
                state: { status: 'Loading' }
            }
        }
    }),
    on(userActions.FetchSubscriptionPlansSuccessAction, (state: IUserState, props: { response: IResponse<ISubscriptionPlanWithTaxAndDiscountInfo[]> }) => {
        return {
            ...state,
            SubscriptionPlans: {
                state: { status: 'Success' },
                plans: props.response.Value
            }
        }
    }),
    on(userActions.FetchSubscriptionPlansFailureAction, (state: IUserState, props: { error: IErrors }) => {
        return {
            ...state,
            SubscriptionPlans: {
                ...state.SubscriptionPlans,
                state: { status: 'Failure', error: props?.error?.Errors?.[0]?.Message || 'Failed to load Subscription Plans' }
            }
        }
    }),
    // New Orders-related handlers
    on(userActions.FetchOrdersAction, (state: IUserState) => {
        return {
            ...state,
            Orders: {
                ...state.Orders,
                state: { status: 'Loading' }
            }
        };
    }),
    on(userActions.FetchOrdersSuccessAction, (state: IUserState, { response, paymentStatus, orderStatus }) => {
        const key = `${response.PageNumber}_${response.PageSize}_${orderStatus || 'all'}_${paymentStatus || 'all'}`;
        return {
            ...state,
            Orders: {
                ...state.Orders,
                state: { status: 'Success' },
                orders: {
                    ...state.Orders.orders,
                    [key]: response.Value || []
                },
                pagination: {
                    pageNumber: response.PageNumber,
                    pageSize: response.PageSize,
                    totalCount: response.TotalCount,
                    totalPages: response.TotalPages
                }
            }
        };
    }),
    on(userActions.FetchOrdersFailureAction, (state: IUserState, props: { error: IErrors }) => {
        return {
            ...state,
            Orders: {
                ...state.Orders,
                state: { status: 'Failure', error: props?.error?.Errors?.[0]?.Message || 'Failed to load Orders' }
            }
        };
    }),
    on(userActions.ClearOrdersStateAction, (state: IUserState) => {
        return {
            ...state,
            Orders: {
                state: { status: 'NotFetched' },
                orders: {},
                pagination: null
            }
        };
    }),
    // New handler for updating orders pagination
    on(userActions.BillingHistoryUpdatePaginationAction, (state: IUserState, { pageNumber, pageSize }) => {
        return {
            ...state,
            Orders: {
                ...state.Orders,
                pagination: {
                    pageNumber,
                    pageSize,
                    totalCount: state.Orders.pagination?.totalCount ?? 0,
                    totalPages: state.Orders.pagination?.totalPages ?? 0
                }
            }
        };
    })
);

const selectUserState = createFeatureSelector<IUserState>("user");

export const selectUserInfo = createSelector(
    selectUserState,
    (state: IUserState) => state.UserInfo
)
export const selectLoading = createSelector(
    selectUserState,
    (state: IUserState) => state.loading
);

export const selectSettings = createSelector(
    selectUserState,
    (state: IUserState) => state.Settings
);

export const selectIsNewUser = createSelector(
    selectUserState,
    (state: IUserState) => state.IsNewUser
)

export const selectBookWithChapterById = (bookId: string) =>
    createSelector(selectUserState, (state: IUserState | undefined) =>
        state && state.BookWithChapter ? state.BookWithChapter[bookId] ?? null : null
    );

export const selectChapterWithPagesById = (chapterId: string) =>
    createSelector(selectUserState, (state: IUserState | undefined) =>
        state && state.ChapterWithPages ? state.ChapterWithPages[chapterId] ?? null : null
    );

// Selector for books on a specific page
export const selectUserBooksByPage = (pageNumber: number, pageSize: number) =>
    createSelector(selectUserState, (state: IUserState) => {
        const key = `${pageNumber}_${pageSize}`;
        return state.Books[key] || []; // Return empty array instead of null
    });

// Selector for pagination metadata
export const selectBooksPagination = createSelector(
    selectUserState,
    (state: IUserState) => state.Pagination
);
// Selector for all books (if needed)
export const selectAllUserBooks = createSelector(
    selectUserState,
    (state: IUserState) => {
        return Object.values(state.Books).flat();
    }
);
// Selector for Subscription Plans
export const selectSubscriptionPlans = createSelector(
    selectUserState,
    (state: IUserState) => state.SubscriptionPlans
)

// Selector for orders on a specific page with filters
export const selectOrdersByPageAndFilters = (pageNumber: number, pageSize: number, orderStatus?: string, paymentStatus?: string) =>
    createSelector(selectUserState, (state: IUserState) => {
        const key = `${pageNumber}_${pageSize}_${orderStatus || 'all'}_${paymentStatus || 'all'}`;
        return state.Orders.orders[key] || [];
    });

// Selector for orders pagination metadata
export const selectOrdersPagination = createSelector(
    selectUserState,
    (state: IUserState) => state.Orders.pagination
);

// Selector for orders async state
export const selectOrdersState = createSelector(
    selectUserState,
    (state: IUserState) => state.Orders.state
);

// Selector for all orders (if needed)
export const selectAllOrders = createSelector(
    selectUserState,
    (state: IUserState) => {
        return Object.values(state.Orders.orders).flat();
    }
);
// Selector For Getting the Billing History Pagination State
export const selectBillingHistoryPaingation = createSelector(
    selectUserState,
    (state: IUserState) => {
        return state.Orders.pagination
    }
)